import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Chip,
  TextField,
} from "@mui/material";
import { DragHandle } from "@mui/icons-material";

export function SortableListContainer({
  title,
  action,
  items,
  onChange,
  emptyMessage,
  includeCheckboxes = true,
  editable,
  render,
}) {
  // Track the index of the item currently being dragged
  const [draggedIndex, setDraggedIndex] = useState(null);
  // Track the index of the item currently hovered over
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Derived state: Sort items - draggable items first (by sequence), then non-draggable items at bottom
  const sortedItems = useMemo(() => {
    const draggable = [];
    const notDraggable = [];
    (items || []).forEach((item) => {
      if (item.notDraggable) {
        notDraggable.push(item);
      } else {
        draggable.push(item);
      }
    });
    draggable.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    notDraggable.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    return [...draggable, ...notDraggable];
  }, [items]);

  // Drag and Drop Handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (index) => {
    setDragOverIndex(null);
    if (draggedIndex === null || draggedIndex === index) return;

    // Block dropping onto or around non-draggable items
    const targetItem = sortedItems[index];
    const draggedItem = sortedItems[draggedIndex];
    if (targetItem?.notDraggable || draggedItem?.notDraggable) return;

    const reorderedItems = [...sortedItems];
    const [movedItem] = reorderedItems.splice(draggedIndex, 1);
    reorderedItems.splice(index, 0, movedItem);

    // Recalculate sequences sequentially to maintain clean indexing
    const updatedItems = reorderedItems.map((item, i) => ({
      ...item,
      sequence: i,
    }));

    onChange(updatedItems);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Checkbox State Toggle Handler
  const handleCheckboxChange = (itemId) => {
    const updatedItems = sortedItems.map((item) =>
      item.id === itemId ? { ...item, selected: !item.selected } : item,
    );
    onChange(updatedItems);
  };

  // Generic handler for arbitrary item updates (passed to renderItemExtra)
  const handleItemChange = (itemId, update) => {
    const updatedItems = sortedItems.map((item) =>
      item.id === itemId ? { ...item, ...update } : item,
    );
    onChange(updatedItems);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {title} ({items?.length || 0})
        </Typography>
        {action}
      </Box>

      <Box
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          pt: 0.25,
        }}
      >
        {sortedItems.length > 0 ? (
          sortedItems.map((item, index) => {
            const isTarget = dragOverIndex === index && draggedIndex !== null;
            const isMovingUp = draggedIndex > index;

            return (
              <Box
                key={item.id}
                draggable={!item.notDraggable}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => {
                  if (item.notDraggable) return;
                  handleDragOver(e, index);
                }}
                onDragLeave={handleDragLeave}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                  transition: "opacity 0.2s ease, border-color 0.1s ease",
                  px: 2,
                  opacity: item.notDraggable
                    ? 0.45
                    : draggedIndex === index
                      ? 0.4
                      : 1,
                  minHeight: "40px",
                  // Pre-allocated invisible borders to handle the drop line without shaking the layout
                  borderTop: "1px solid transparent",
                  borderBottom: "1px solid transparent",
                  borderTopColor: index === 0 ? "transparent" : "divider",

                  // Dynamically illuminate the drop target border line
                  ...(isTarget
                    ? {
                        [isMovingUp ? "borderTopColor" : "borderBottomColor"]:
                          "info.main",
                        [isMovingUp ? "borderTop" : "borderBottom"]:
                          "2px solid",
                      }
                    : {}),

                  // Prevents inner text/checkbox nodes from hijacking drag events and causing flickering
                  "& > *": {
                    pointerEvents: draggedIndex !== null ? "none" : "auto",
                  },
                }}
              >
                <DragHandle
                  sx={{
                    fontSize: "18px !important",
                    cursor: item.notDraggable
                      ? "default"
                      : draggedIndex === index
                        ? "grabbing"
                        : "grab",
                    color: item.notDraggable
                      ? "action.disabled"
                      : "text.secondary",
                    visibility: item.notDraggable ? "hidden" : "visible",
                  }}
                />
                {includeCheckboxes ? (
                  <FormControlLabel
                    label={
                      editable?.name ? (
                        <TextField
                          fullWidth
                          variant="standard"
                          value={item.name}
                          disabled={item.notDraggable}
                          onChange={(e) => {
                            handleItemChange(item.id, { name: e.target.value });
                          }}
                        />
                      ) : (
                        item.name
                      )
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                    }}
                    control={
                      <Checkbox
                        checked={!!item.selected}
                        disabled={item.notDraggable}
                        onChange={() => handleCheckboxChange(item.id)}
                      />
                    }
                  />
                ) : editable?.name ? (
                  <TextField
                    fullWidth
                    variant="standard"
                    value={item.name}
                    onChange={(e) => {
                      handleItemChange(item.id, { name: e.target.value });
                    }}
                  />
                ) : (
                  <Typography variant="h6">{item.name}</Typography>
                )}

                {/* Render any extra UI elements specific to the parent component */}
                {render?.itemExtra &&
                  render?.itemExtra?.(item, handleItemChange)}
              </Box>
            );
          })
        ) : (
          <Typography
            sx={{ fontSize: "14px", color: "secondary", px: 1.5, py: 1 }}
            color="secondary"
          >
            {emptyMessage}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
